import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowSquareOut, CalendarBlank, Prohibit } from "@phosphor-icons/react";
import { Modal, In, Sel } from "./shared";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const SectionLabel = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF5722] font-bold mb-3">{children}</p>
);

export function PlayoffMatchModal({ division, match, bracket, onClose, onSaved }) {
  const teams = bracket?.teams || {};
  const nameOf = (tid) => teams[tid]?.name || "TBD";
  const seedList = Object.entries(teams).sort((x, y) => (x[1].seed || 99) - (y[1].seed || 99));

  const [a, setA] = useState(match.a_score ?? "");
  const [b, setB] = useState(match.b_score ?? "");
  const [date, setDate] = useState(toLocalInput(match.date));
  const [court, setCourt] = useState(match.court || "");
  const [venue, setVenue] = useState(match.venue || "");
  const [teamA, setTeamA] = useState(match.a_team_id || "");
  const [teamB, setTeamB] = useState(match.b_team_id || "");
  const [busy, setBusy] = useState(false);

  const bothSet = match.a_team_id && match.b_team_id;
  const isFirstRound = (match.round || 0) === 0;
  const hasGame = !!match.game_id;

  const put = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = await api.put(`/playoffs/${encodeURIComponent(division)}/match/${match.id}`, body);
      toast.success(okMsg);
      onSaved(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveSchedule = () => {
    const body = { court, venue };
    if (date) body.date = new Date(date).toISOString();
    put(body, hasGame ? "Game info updated — Scorekeeper game updated too" : "Game info saved");
  };

  const saveResult = (complete) => put({
    a_score: a === "" ? null : parseInt(a, 10),
    b_score: b === "" ? null : parseInt(b, 10),
    status: complete ? "completed" : "pending",
  }, complete ? "Result saved — winner advanced" : "Game reopened");

  const saveTeams = () => {
    if (match.status === "completed" && !window.confirm("Changing teams will clear this matchup's result. Continue?")) return;
    put({ a_team_id: teamA, b_team_id: teamB }, "Matchup updated");
  };

  const sendToScorekeeper = async () => {
    if (!date) { toast.error("Set a date & time first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/playoffs/${encodeURIComponent(division)}/match/${match.id}/game`, {
        date: new Date(date).toISOString(), court, venue,
      });
      toast.success("Game created in the Scorekeeper");
      window.open(`/admin/score/${r.data.game_id}`, "_blank", "noopener");
      onSaved(r.data.bracket);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancelGame = async () => {
    if (!window.confirm("Cancel this scheduled playoff game? It will be removed from the Scorekeeper and its date/court cleared. Any result already on the bracket stays.")) return;
    setBusy(true);
    try {
      if (match.game_id) await api.delete(`/games/${match.game_id}`);
      const r = await api.put(`/playoffs/${encodeURIComponent(division)}/match/${match.id}`, { date: "", court: "", venue: "" });
      toast.success("Playoff game cancelled");
      onSaved(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`${nameOf(match.a_team_id)} vs ${nameOf(match.b_team_id)}`}>
      <div className="space-y-6" data-testid="playoffs-match-modal">
        {/* Game info */}
        <div>
          <SectionLabel>Date, Time & Court</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <In label="Date & time" type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="playoffs-match-date" />
            <In label="Court / Gym" type="text" value={court} onChange={(e) => setCourt(e.target.value)} placeholder="e.g. Court 1" data-testid="playoffs-match-court" />
          </div>
          <div className="mt-3">
            <In label="Venue (optional)" type="text" value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="e.g. IEFSC Main Gym" data-testid="playoffs-match-venue" />
          </div>
          <button onClick={saveSchedule} disabled={busy} data-testid="playoffs-save-schedule-btn" className="mt-3 w-full border border-[#FF5722]/60 text-[#FF5722] hover:bg-[#FF5722]/10 font-bold uppercase tracking-wide py-2.5 text-sm transition-all duration-200 flex items-center justify-center gap-2">
            <CalendarBlank size={15} weight="fill" /> Save Game Info
          </button>
        </div>

        {/* Scorekeeper link */}
        {bothSet && (
          <div className="border-t border-white/10 pt-5">
            <SectionLabel>Scorekeeper</SectionLabel>
            {hasGame ? (
              <div className="space-y-2">
                <button onClick={() => window.open(`/admin/score/${match.game_id}`, "_blank", "noopener")} data-testid="playoffs-open-scorekeeper-btn"
                  className="w-full bg-[#00E676]/15 border border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/25 font-bold uppercase tracking-wide py-2.5 text-sm transition-all duration-200 flex items-center justify-center gap-2">
                  <ArrowSquareOut size={15} /> Open in Scorekeeper
                </button>
                <button onClick={cancelGame} disabled={busy} data-testid="playoffs-cancel-game-btn"
                  className="w-full border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 font-bold uppercase tracking-wide py-2.5 text-sm transition-all duration-200 flex items-center justify-center gap-2">
                  <Prohibit size={15} /> Cancel Scheduled Game
                </button>
              </div>
            ) : (
              <>
                <p className="text-white/40 text-xs mb-2">Creates this game in the Scorekeeper so you can score it live. The final score will update this bracket automatically.</p>
                <button onClick={sendToScorekeeper} disabled={busy || !date} data-testid="playoffs-send-scorekeeper-btn"
                  className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 text-black font-bold uppercase tracking-wide py-2.5 text-sm transition-all duration-200 flex items-center justify-center gap-2">
                  <ArrowSquareOut size={15} /> {date ? "Send to Scorekeeper" : "Set a date first"}
                </button>
              </>
            )}
          </div>
        )}

        {/* Result */}
        {bothSet && (
          <div className="border-t border-white/10 pt-5">
            <SectionLabel>Enter Result Manually</SectionLabel>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5 truncate">{nameOf(match.a_team_id)}</label>
                <input type="number" min={0} value={a} onChange={(e) => setA(e.target.value)} data-testid="playoffs-score-a" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-center font-display text-2xl focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5 truncate">{nameOf(match.b_team_id)}</label>
                <input type="number" min={0} value={b} onChange={(e) => setB(e.target.value)} data-testid="playoffs-score-b" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-center font-display text-2xl focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
              </div>
            </div>
            <p className="text-white/40 text-xs mt-2">Marking the game complete sets the winner and advances them to the next round. You can reopen a game to correct a score.</p>
            <div className="flex gap-3 pt-3">
              {match.status === "completed" && (
                <button onClick={() => saveResult(false)} disabled={busy} data-testid="playoffs-reopen-btn" className="flex-1 border border-white/20 text-white/80 hover:bg-white/5 font-bold uppercase tracking-wide py-2.5 text-sm transition-colors">Reopen</button>
              )}
              <button onClick={() => saveResult(true)} disabled={busy || a === "" || b === ""} data-testid="playoffs-complete-btn" className="flex-1 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-2.5 text-sm transition-all duration-200">
                {busy ? "Saving…" : "Mark Complete"}
              </button>
            </div>
          </div>
        )}

        {/* Team correction (first round only) */}
        {isFirstRound && (
          <div className="border-t border-white/10 pt-5">
            <SectionLabel>Fix Teams (First Round)</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Sel label="Team A" value={teamA} onChange={(e) => setTeamA(e.target.value)} data-testid="playoffs-team-a-select">
                {seedList.map(([tid, t]) => <option key={tid} value={tid}>{`#${t.seed} ${t.name}`}</option>)}
              </Sel>
              <Sel label="Team B" value={teamB} onChange={(e) => setTeamB(e.target.value)} data-testid="playoffs-team-b-select">
                {seedList.map(([tid, t]) => <option key={tid} value={tid}>{`#${t.seed} ${t.name}`}</option>)}
              </Sel>
            </div>
            <p className="text-white/40 text-xs mt-2">Swapping in a team already placed in another matchup isn't allowed — clear it there first. Changing teams resets this matchup's result.</p>
            <button onClick={saveTeams} disabled={busy || !teamA || !teamB || (teamA === match.a_team_id && teamB === match.b_team_id)} data-testid="playoffs-save-teams-btn"
              className="mt-3 w-full border border-white/20 text-white/80 hover:bg-white/5 disabled:opacity-40 font-bold uppercase tracking-wide py-2.5 text-sm transition-colors">
              Save Teams
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
